/**
 * @file     glass.ts
 * @readme   ./README.md
 * @purpose  Centralized glassmorphism, gradient, and backdrop-filter tokens. All gradient opacities, blur values, and glass surface styles are defined here so the entire app renders a consistent atmospheric background system.
 */

// ── Backdrop-filter presets ─────────────────────────────────
// Ordered by visual weight. Components pick the tier that matches
// their z-depth and surface area.

export const glassBlur = {
  /** Subtle, small chips, badges, status indicators */
  subtle: 'blur(8px)',
  /** Medium, cards, drawers, interactive blocks */
  medium: 'blur(14px)',
  /** Strong, navbar, announcement bar, modal overlays */
  strong: 'blur(18px) saturate(1.06)',
  /** Strong (scrolled), navbar after scroll threshold */
  strongScrolled: 'blur(22px) saturate(1.10)',
} as const;

// ── Glass surface background builder ────────────────────────
// Returns a semi-transparent background color for glass surfaces.
// Use with glassBlur to create consistent glass panels.

/**
 * Build a glass surface background.
 * @param alphaFn  - MUI `alpha()` function (passed to avoid import in every file)
 * @param surface  - raw hex surface color (e.g. theme.rawSemanticColors.surfaceCard)
 * @param opacity  - surface opacity (default 0.6 for dark, 0.72 for light)
 */
export const glassSurface = (
  alphaFn: (color: string, opacity: number) => string,
  surface: string,
  opacity = 0.6,
) => alphaFn(surface, opacity);

// ── Glass material system ───────────────────────────────────
// Apple-style materials for the control and navigation layer that floats
// above content. Values per mode live in CSS custom properties written by
// the theme boot styles, so one `glassMaterial(tier)` works in light and dark,
// in Emotion `sx`, in theme overrides and (via the `.r-glass-<tier>` classes)
// in Emotion-free CSS modules. Never glass a text-heavy content card.

export type GlassTier = 'ultraThin' | 'thin' | 'regular' | 'thick' | 'chrome';

export type GlassEdge = 'all' | 'top' | 'bottom';

type GlassMode = 'light' | 'dark';

export interface GlassLensSpec {
  /** Displacement as a fraction of the element's box. */
  scale: number;
  /** Width of the lens band at each edge, as a fraction of the box. */
  band: number;
  /** Horizontal strength (0 to 1), to keep wide bars from bending too far sideways. */
  x?: number;
}

type PerMode<T> = Record<GlassMode, T>;

export interface GlassTierSpec {
  /** Backdrop blur radius in px. Light glass stays sharp: it reads through lensing and specular, not frost. */
  blur: PerMode<number>;
  /** Backdrop saturate factor: how much colour the material picks up. */
  saturate: PerMode<number>;
  /** Tint opacity over the surface colour. Low by design. */
  fill: PerMode<number>;
  /**
   * Vibrancy: brightness and contrast applied to what is behind. Light glass
   * lifts and compresses it so dark text reads; dark glass sinks it so light
   * text reads. Legibility comes from here, never from text-shadow.
   */
  vibrancy: PerMode<number>;
  contrast: PerMode<number>;
  /** Edge refraction (Chromium only). */
  lens: PerMode<GlassLensSpec | null>;
  /** Outer lift shadow: y offset, blur, opacity (light, dark). */
  lift: { y: number; blur: number; light: number; dark: number };
  /** Where the edge draws. Bars only rule the edge facing content. */
  edge: GlassEdge;
}

export const GLASS_TIERS: readonly GlassTier[] = ['ultraThin', 'thin', 'regular', 'thick', 'chrome'];

export const GLASS_MODES: readonly GlassMode[] = ['light', 'dark'];

export const glassTiers: Record<GlassTier, GlassTierSpec> = {
  /** Small controls over photos: card pills, card icon buttons. The showcase. */
  ultraThin: {
    blur: { light: 3, dark: 6 }, saturate: { light: 2.2, dark: 1.9 }, fill: { light: 0.08, dark: 0.2 },
    vibrancy: { light: 1.25, dark: 0.72 }, contrast: { light: 0.75, dark: 1 },
    lens: { light: { scale: 0.5, band: 0.34 }, dark: { scale: 0.32, band: 0.28 } },
    lift: { y: 3, blur: 10, light: 0.24, dark: 0.45 }, edge: 'all',
  },
  /** Floating map controls, toasts, small banners. */
  thin: {
    blur: { light: 5, dark: 10 }, saturate: { light: 2.1, dark: 1.9 }, fill: { light: 0.1, dark: 0.28 },
    vibrancy: { light: 1.22, dark: 0.7 }, contrast: { light: 0.78, dark: 1 },
    lens: { light: { scale: 0.36, band: 0.28 }, dark: { scale: 0.2, band: 0.2 } },
    lift: { y: 6, blur: 20, light: 0.2, dark: 0.45 }, edge: 'all',
  },
  /**
   * Menus, popovers, tooltips, toasts: they carry text, so the interior is
   * calm (compressed and lifted in light, sunk in dark) while the lens and rim
   * keep the edge liquid.
   */
  regular: {
    blur: { light: 14, dark: 14 }, saturate: { light: 1.9, dark: 1.9 }, fill: { light: 0.7, dark: 0.55 },
    vibrancy: { light: 1.25, dark: 0.55 }, contrast: { light: 0.6, dark: 1 },
    lens: { light: { scale: 0.1, band: 0.12 }, dark: { scale: 0.06, band: 0.08 } },
    lift: { y: 12, blur: 36, light: 0.2, dark: 0.55 }, edge: 'all',
  },
  /** Sheets, dialogs and the mobile menu: forms and lists, content behind softly visible. */
  thick: {
    blur: { light: 18, dark: 20 }, saturate: { light: 1.9, dark: 1.9 }, fill: { light: 0.72, dark: 0.58 },
    vibrancy: { light: 1.3, dark: 0.62 }, contrast: { light: 0.6, dark: 1 },
    lens: { light: { scale: 0.05, band: 0.07 }, dark: { scale: 0.03, band: 0.05 } },
    lift: { y: 20, blur: 60, light: 0.24, dark: 0.6 }, edge: 'all',
  },
  /**
   * The header and docked bars: content scrolls under them and text sits on
   * them, so this is Apple's heavier bar material, paired with the scroll-edge
   * band in AppFrame. The lens only hints at the edge.
   */
  chrome: {
    blur: { light: 22, dark: 22 }, saturate: { light: 1.8, dark: 1.8 }, fill: { light: 0.62, dark: 0.6 },
    vibrancy: { light: 1.3, dark: 0.42 }, contrast: { light: 0.55, dark: 0.85 },
    lens: { light: { scale: 0.06, band: 0.1, x: 0.04 }, dark: { scale: 0.04, band: 0.1, x: 0.04 } },
    lift: { y: 0, blur: 0, light: 0, dark: 0 }, edge: 'bottom',
  },
};

/** The colours a material is built from: raw hex values from the palette. */
export interface GlassPaletteInput {
  surface: string;
  textPrimary: string;
  /** Strong rule used as the edge under prefers-contrast: more. */
  contrastEdge: string;
  /** Dark tint for inverse glass (tooltips) carrying white text. */
  inverse: string;
}

const rgba = (hex: string, opacity: number): string => {
  const n = parseInt(hex.slice(1, 7), 16);
  return `rgba(${n >> 16}, ${(n >> 8) & 255}, ${n & 255}, ${opacity})`;
};

const WHITE = '#FFFFFF';
const BLACK = '#000000';

type GlassPart = 'fill' | 'fillHover' | 'filter' | 'edge' | 'sheen' | 'shadow' | 'shadowHover' | 'solid';

const partVar = (part: GlassPart) => part.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

export const glassVar = (tier: GlassTier | 'inverse', part: GlassPart) => `var(--glass-${tier}-${partVar(part)})`;

/** Id of the SVG lens filter for a tier and mode, rendered once by GlassLensDefs. */
export const glassLensId = (tier: GlassTier, mode: GlassMode) => `r-glass-lens-${tier}-${mode}`;

/**
 * The curved-lens rim, drawn with inset shadows so it never fights a
 * component's own border or pseudo-elements: a bright top highlight, a softer
 * bottom rim, a faint ring, a top inner glow and a bottom inner shade.
 */
const rimShadows = (mode: GlassMode, boost = 1) => {
  const w = (a: number) => rgba(WHITE, Math.min(1, a * boost));
  if (mode === 'dark') {
    return [
      `inset 0 1px 0 0 ${w(0.3)}`,
      `inset 0 -1px 0 0 ${w(0.1)}`,
      `inset 0 0 0 1px ${w(0.06)}`,
      `inset 0 10px 14px -12px ${w(0.18)}`,
      `inset 0 -10px 16px -12px ${rgba(BLACK, 0.4)}`,
    ];
  }
  // Light: a top-left specular edge and a darker bottom-right inner edge give
  // the rim volume even over a white page, where blur alone shows nothing.
  return [
    `inset 1px 1px 0 0 ${w(0.95)}`,
    `inset -1px -1px 0 0 ${rgba(BLACK, 0.12)}`,
    `inset 0 0 0 1px ${w(0.32)}`,
    `inset 0 12px 16px -12px ${w(0.7)}`,
    `inset 0 -10px 14px -12px ${rgba(BLACK, 0.1)}`,
  ];
};

/** Raw per-mode values for one tier. Exported for tests and the boot CSS. */
export const resolveGlassTier = (tier: GlassTier, mode: GlassMode, colors: GlassPaletteInput) => {
  const spec = glassTiers[tier];
  const isDark = mode === 'dark';
  // Light lift is tinted with the ink colour rather than grey, so the glass
  // floats over white without a dirty halo.
  const shade = isDark ? BLACK : colors.textPrimary;
  const lift = spec.lift.blur > 0
    ? [`0 ${spec.lift.y}px ${spec.lift.blur}px -2px ${rgba(shade, spec.lift[mode])}`, `0 1px 3px 0 ${rgba(shade, spec.lift[mode] / 2)}`]
    : [];
  const fill = spec.fill[mode];

  return {
    fill: rgba(colors.surface, fill),
    fillHover: rgba(colors.surface, Math.min(1, fill + 0.1)),
    // The lens variable is only set on Chromium (data-glass-lens); elsewhere it
    // falls back to nothing and the material is blur + vibrancy alone.
    filter: `blur(${spec.blur[mode]}px) saturate(${spec.saturate[mode]}) contrast(${spec.contrast[mode]}) brightness(${spec.vibrancy[mode]}) var(--glass-${tier}-lens,)`,
    // Bars rule the edge facing content with a hairline; floating glass wears
    // a bright rim that catches light.
    edge: tier === 'chrome'
      ? (isDark ? rgba(WHITE, 0.1) : rgba(colors.textPrimary, 0.08))
      : rgba(WHITE, isDark ? 0.16 : 0.6),
    // Light: a specular arc lit from the top left and a faint caustic at the
    // bottom right. Dark keeps a plain top-lit sheen.
    sheen: isDark
      ? `linear-gradient(180deg, ${rgba(WHITE, 0.08)} 0%, ${rgba(WHITE, 0)} 42%, ${rgba(WHITE, 0)} 72%, ${rgba(WHITE, 0.03)} 100%)`
      : `radial-gradient(140% 90% at 18% 0%, ${rgba(WHITE, 0.55)} 0%, ${rgba(WHITE, 0)} 46%), radial-gradient(120% 80% at 86% 112%, ${rgba(WHITE, 0.24)} 0%, ${rgba(WHITE, 0)} 42%)`,
    shadow: [...rimShadows(mode), ...lift].join(', '),
    shadowHover: [...rimShadows(mode, 1.4), ...lift].join(', '),
    solid: colors.surface,
  };
};

/** CSS custom property declarations for every tier in one mode. */
export const createGlassCssVars = (mode: GlassMode, colors: GlassPaletteInput): string => {
  const declarations = GLASS_TIERS.flatMap((tier) => {
    const resolved = resolveGlassTier(tier, mode, colors);
    return (Object.keys(resolved) as GlassPart[]).map(
      (part) => `--glass-${tier}-${partVar(part)}:${resolved[part]}`,
    );
  });
  declarations.push(
    `--glass-inverse-fill:${rgba(colors.inverse, 0.62)}`,
    `--glass-inverse-solid:${colors.inverse}`,
    `--glass-contrast-edge:${colors.contrastEdge}`,
    `--glass-scroll-edge:0 6px 20px -12px ${rgba(BLACK, mode === 'dark' ? 0.6 : 0.28)}`,
  );
  return declarations.join(';');
};

/** Lens variables, set only when the boot script marks the browser as Chromium. */
export const createGlassLensCss = (): string =>
  GLASS_MODES.map((mode) => `html[data-glass-lens][data-theme="${mode}"]{${GLASS_TIERS.filter((tier) => glassTiers[tier].lens[mode])
    .map((tier) => `--glass-${tier}-lens:url(#${glassLensId(tier, mode)})`)
    .join(';')}}`).join('');

const mapImage = (axis: 'x' | 'y', band: number, strength = 1) => {
  const level = (v: number) => Math.round(128 + (v - 128) * strength);
  const channel = (v: number) => (axis === 'x' ? `rgb(${level(v)},0,0)` : `rgb(0,${level(v)},0)`);
  const [x2, y2] = axis === 'x' ? ['1', '0'] : ['0', '1'];
  const b = (band * 100).toFixed(1);
  const bi = (100 - band * 100).toFixed(1);
  const half = (band * 50).toFixed(1);
  const halfI = (100 - band * 50).toFixed(1);
  // Convex profile: strongest bend at the very edge, easing to none inside.
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" preserveAspectRatio="none"><defs><linearGradient id="g" x1="0" y1="0" x2="${x2}" y2="${y2}"><stop offset="0%" stop-color="${channel(255)}"/><stop offset="${half}%" stop-color="${channel(170)}"/><stop offset="${b}%" stop-color="${channel(128)}"/><stop offset="${bi}%" stop-color="${channel(128)}"/><stop offset="${halfI}%" stop-color="${channel(86)}"/><stop offset="100%" stop-color="${channel(0)}"/></linearGradient></defs><rect width="100" height="100" fill="url(#g)"/></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/** The SVG filter primitives for one tier's lens in one mode: an edge displacement map. */
export const glassLensFilter = (tier: GlassTier, mode: GlassMode) => {
  const lens = glassTiers[tier].lens[mode];
  if (!lens) return null;
  return {
    id: glassLensId(tier, mode),
    scale: lens.scale,
    xMap: mapImage('x', lens.band, lens.x ?? 1),
    yMap: mapImage('y', lens.band),
  };
};

const NO_BACKDROP = '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))';
const REDUCED_TRANSPARENCY = '@media (prefers-reduced-transparency: reduce)';
const MORE_CONTRAST = '@media (prefers-contrast: more)';

export interface GlassMaterialOptions {
  /** Inverse glass: a dark tint for white text (tooltips), in both modes. */
  inverse?: boolean;
  /** Drop the lift and rim shadow, e.g. when a component owns its shadow. */
  flat?: boolean;
  /** Override the tier's edge, e.g. 'top' for a bottom-docked bar. */
  edge?: GlassEdge;
  /** Light response: hover brightens the rim, press sinks the glass. */
  interactive?: boolean;
}

/** Hover and press light response, for a glass element or a glass child. */
export const glassInteraction = (tier: GlassTier, target = '&') => ({
  transition: 'transform 140ms cubic-bezier(0.2, 0, 0, 1), box-shadow 140ms cubic-bezier(0.2, 0, 0, 1), background-color 140ms cubic-bezier(0.2, 0, 0, 1)',
  '@media (hover: hover)': {
    [`&:hover${target === '&' ? '' : ` ${target}`}`]: {
      backgroundColor: glassVar(tier, 'fillHover'),
      boxShadow: glassVar(tier, 'shadowHover'),
    },
  },
  [`&:active${target === '&' ? '' : ` ${target}`}`]: { transform: 'scale(0.96)' },
  '@media (prefers-reduced-motion: reduce)': {
    [`&:active${target === '&' ? '' : ` ${target}`}`]: { transform: 'none' },
  },
});

/**
 * The one glass primitive. Returns a style object for `sx`, `styled` or a
 * theme override. Opaque surface where backdrop-filter is unsupported, under
 * Reduce Transparency, and under Increase Contrast (with a strong edge).
 */
export const glassMaterial = (tier: GlassTier, options: GlassMaterialOptions = {}) => {
  const { inverse = false, flat = false, interactive = false } = options;
  const spec = glassTiers[tier];
  const edgeSide = options.edge ?? spec.edge;
  const fill = inverse ? glassVar('inverse', 'fill') : glassVar(tier, 'fill');
  const solid = inverse ? glassVar('inverse', 'solid') : glassVar(tier, 'solid');
  const edgeLine = `1px solid ${glassVar(tier, 'edge')}`;
  const edgeProp = edgeSide === 'all' ? 'border' : edgeSide === 'top' ? 'borderTop' : 'borderBottom';
  const edge: Record<string, string> = { [edgeProp]: edgeLine };
  const opaque = {
    backgroundColor: solid, backgroundImage: 'none', backdropFilter: 'none', WebkitBackdropFilter: 'none',
  };

  return {
    backgroundColor: fill,
    backgroundImage: inverse ? 'none' : glassVar(tier, 'sheen'),
    backdropFilter: glassVar(tier, 'filter'),
    WebkitBackdropFilter: glassVar(tier, 'filter'),
    ...edge,
    ...(flat ? {} : { boxShadow: glassVar(tier, 'shadow') }),
    ...(interactive ? glassInteraction(tier) : {}),
    [NO_BACKDROP]: opaque,
    [REDUCED_TRANSPARENCY]: opaque,
    [MORE_CONTRAST]: { ...opaque, borderColor: 'var(--glass-contrast-edge)' },
  };
};

const toKebab = (key: string) =>
  key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`).replace(/^webkit-/, '-webkit-');

const declarationsOf = (style: Record<string, unknown>) =>
  Object.entries(style)
    .filter(([, value]) => typeof value === 'string')
    .map(([key, value]) => `${toKebab(key)}:${value as string}`)
    .join(';');

/**
 * Global `.r-glass-<tier>` classes built from the same `glassMaterial` objects,
 * for Emotion-free CSS modules and plain DOM (map controls).
 */
export const createGlassClassCss = (): string =>
  GLASS_TIERS.map((tier) => {
    const style = glassMaterial(tier) as Record<string, unknown>;
    const selector = `.r-glass-${tier}`;
    const base = `${selector}{${declarationsOf(style)}}`;
    const wrapped = [NO_BACKDROP, REDUCED_TRANSPARENCY, MORE_CONTRAST]
      .map((query) => `${query}{${selector}{${declarationsOf(style[query] as Record<string, unknown>)}}}`)
      .join('');
    return base + wrapped;
  }).join('') + createGlassLensCss();

// ── Hero gradient builders ──────────────────────────────────
// Standardized gradient patterns used across hero sections and
// page-level backgrounds. Each accepts raw color values and
// returns a CSS background string.

export const heroGradient = {
  /**
   * Top-to-bottom fade from muted primary into surface card.
   * Used by: HeroSection, properties hero, blog hero
   */
  primaryFade: (
    alphaFn: (c: string, o: number) => string,
    primaryMuted: string,
    surfaceCard: string,
    intensity = 0.85,
  ) => `linear-gradient(180deg, ${alphaFn(primaryMuted, intensity)} 0%, ${surfaceCard} 100%)`,

  /**
   * Diagonal gradient from muted primary into secondary accent.
   * Used by: PropertyCard, BlogCard, BlogPostDetail overlays
   */
  cardOverlay: (
    alphaFn: (c: string, o: number) => string,
    primaryMuted: string,
    secondary: string,
    primaryIntensity = 0.75,
    secondaryIntensity = 0.15,
  ) => `linear-gradient(135deg, ${alphaFn(primaryMuted, primaryIntensity)} 0%, ${alphaFn(secondary, secondaryIntensity)} 100%)`,

  /**
   * Multi-stop landing hero gradient (dark, deep brand colors).
   * Used by: LandingHeroSSR, page.tsx
   */
  landingHero: (
    darkest: string,
    mid: string,
    lightest: string,
  ) => `linear-gradient(135deg, ${darkest} 0%, ${mid} 50%, ${lightest} 100%)`,
} as const;

// ── Radial orb accent ───────────────────────────────────────
// Soft radial glow used behind hero sections for atmospheric depth.

export const heroOrb = (
  alphaFn: (c: string, o: number) => string,
  secondary: string,
  primary: string,
) => `radial-gradient(circle, ${alphaFn(secondary, 0.06)} 0%, ${alphaFn(secondary, 0.04)} 15%, ${alphaFn(primary, 0.03)} 30%, ${alphaFn(primary, 0.015)} 50%, transparent 70%)`;

// ── Gradient opacity reference ──────────────────────────────
// These are the canonical alpha ranges. Components should stay
// within these bounds to maintain visual consistency.
//
// Background atmosphere:  0.015 – 0.07
// Hero section fills:     0.75 – 0.90
// Card overlays:          0.15 – 0.80
// Orb accents:            0.015 – 0.06
// Directional light:      0.015 – 0.03
